import { NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { getBuildIdQuery, IImage, IModelViewExterior } from '@ncg/data';
import { TranslatePipe } from '@ngx-translate/core';
import ThreeSixty from '../../3rd-party/mladenilic-threesixty.class';
import { FeatureDetectionService } from '../../core/feature-detection.service';
import { SpinnerModule } from '../../spinner/spinner.module';
import { HdAspect } from '../../utils/helpers/aspect-ratio';
import { ModelViewerLeadComponent } from './model-viewer-lead.component';

@Component({
    selector: 'ncg-model-viewer-exterior',
    template: `
        <div class="exterior-view columns is-flex-direction-column">
            <div class="column">
                <div class="exterior-view__container">
                    <div class="exterior-view__overlay" aria-hidden="true">
                        <img [src]="'/assets/images/360-overlay.png' + buildIdQuery" alt="overlay image" />
                        <p class="exterior-view__text mb-0">{{ 'model_viewer_spot.overlay_title' | translate }}</p>
                    </div>
                    <div class="is-ratio-hd">
                        <div class="exterior-view__item is-flex is-align-items-flex-center is-justify-content-center" #view>
                            <ncg-spinner *ngIf="isLoading" [active]="isLoading" [isCentered]="true"></ncg-spinner>
                        </div>
                    </div>
                </div>
                <div #imageContainer aria-hidden="true" class="is-hidden"></div>
            </div>
            <div class="column pt-0" *ngIf="leadImages?.length">
                <ncg-model-viewer-lead
                    (leadClick)="onLeadImageClicked($event)"
                    [arr]="leadImages"
                    [title]="selectedExterior?.title"></ncg-model-viewer-lead>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, SpinnerModule, ModelViewerLeadComponent, NgIf],
})
export class ModelViewerExteriorComponent implements OnDestroy, AfterViewInit {
    private readonly selectedExteriorIndex = 0;
    private readonly imageWidth = 1220;
    private readonly imageHeight = 686;
    private readonly imageSize = `?width=${this.imageWidth}&height=${this.imageHeight}`;

    private threeSixty?: ThreeSixty;

    public readonly buildIdQuery = getBuildIdQuery();

    public selectedExterior!: IModelViewExterior;
    public isLoading = true;
    public leadImages: IImage[] = [];

    @Input()
    public exteriorView: IModelViewExterior[] = [];

    @Input()
    public index = 0;

    @ViewChild('view', { static: true })
    public readonly view!: ElementRef<HTMLDivElement>;

    @ViewChild('imageContainer', { static: true })
    public readonly imageContainer!: ElementRef<HTMLDivElement>;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly featureDetectionService: FeatureDetectionService
    ) {}

    public ngOnDestroy(): void {
        this.threeSixty?.destroy();
        this.cleanUp();
    }

    public ngAfterViewInit(): void {
        const exteriorView = this.exteriorView?.[0];
        if (exteriorView) {
            this.selectedExterior = exteriorView;
        }

        if (this.featureDetectionService.isBrowser()) {
            this._createImageLeads();
            this.isLoading = true;
            this.loadImages().catch(console.error);
        }
    }

    public onLeadImageClicked(index: number) {
        if (this.selectedExteriorIndex === index) {
            return;
        }

        this.selectedExterior = this.exteriorView[index];
        this.isLoading = true;
        this.loadImages().catch(console.error);
    }

    private _createImageLeads() {
        const images: IImage[] = [];
        this.exteriorView.forEach((view) => {
            if (view.leadInImage) {
                images.push(view.leadInImage);
            }
        });

        this.leadImages = images;
        this.cd.detectChanges();
    }

    private async loadImages() {
        this.cleanUp();
        await Promise.all(
            this.selectedExterior.modelImages.map((image) => {
                const img = new Image();
                img.src = image.url + this.imageSize;
                img.style.display = 'none';
                img.setAttribute('aria-hidden', 'true');
                this.imageContainer.nativeElement.appendChild(img);
                return img.decode();
            })
        );

        this.createViewer();

        this.isLoading = false;
        this.cd.markForCheck();
    }

    private createViewer() {
        this.threeSixty?.destroy();

        const element = this.view.nativeElement;
        const images = this.selectedExterior.modelImages?.map((item) => item.url + this.imageSize);

        if (element && images) {
            this.threeSixty = new ThreeSixty(element, {
                image: images,
                aspectRatio: HdAspect,
                speed: 80,
                inverted: true,
            });
        }
    }

    private cleanUp() {
        while (this.imageContainer.nativeElement.firstChild) {
            this.imageContainer.nativeElement.firstChild.remove();
        }
    }
}
